import _ from "lodash";
import React, { useEffect, useRef, useState } from "react";

import { Button } from "@toddle-edu/ds-web";
import { FormTextInput } from "UIComponents/InputTextField";
import PhoneInputField from "UIComponents/PhoneInputField";
import { RefreshArrowOutlined } from "@toddle-edu/ds-icons";

import classes from "./LoginFormV3.scss";

const styles = {
  editContainerStyle: {
    display: "block",
  },
};

const LoginFormV3 = props => {
  const {
    role,
    userType,
    updateInputField,
    updateCurrentScreen,
    customStyle: { headerStyleV2 } = {},
    renderBackButtonComponent,
    isOrgWorkspaceLoginPage,
    renderTradeMarkText,
    data: {
      email = "",
      phoneNo = "",
      otp = "",
      password = "",
      shouldShowOTPField = false,
      shouldShowPasswordField = false,
      shouldRenderPhoneInput = false,
      shouldShowChangeEmailOption = false,
      resendTimer,
    },
    renderErrorComponent,
    checkEmailVerification,
    generateEmailOTPForSignIn,
    onSignInWithOTPClick,
    onSignInClick,
    isLoginWithPhoneEnabled,
    generateOtpForSignIn,
    verifyParentOTPForSignIn,
    isOTPLoginEnabled,
    isPasswordLoginEnabled,
    showErrorToast,
  } = props;

  const inputRefs = useRef({});
  const [timer, setResendTimer] = useState(resendTimer);
  const [isResendEnabled, setIsResendEnabled] = useState(true);

  const isParent = userType === "parent";
  const isPhoneLoginEnabledForParent = isParent && isLoginWithPhoneEnabled;
  const isEmailAndPhoneLoginEnabled =
    isPhoneLoginEnabledForParent &&
    (isOTPLoginEnabled || isPasswordLoginEnabled);
  const userTypes = ["staff", "student", "parent"];

  const isValid = () => {
    const refKeys = Object.keys(inputRefs.current);
    let errorCount = 0;

    _.forEach(refKeys, key => {
      if (inputRefs.current[key]) {
        errorCount += inputRefs.current[key].isValid();
      }
    });

    return errorCount;
  };

  const updateInputRef = (key, ref) => {
    inputRefs.current[key] = ref;
  };

  const startTimer = () => {
    const timerInterval = setInterval(() => {
      setResendTimer(prevTimer => {
        if (prevTimer <= 0) {
          clearInterval(timerInterval);
          setIsResendEnabled(true);
          updateInputField({ resendTimer: 0 });
          return 0;
        }
        return prevTimer - 1;
      });
    }, 1000);
  };

  const onBackClick = () => {
    const screenMap = {
      staff: role === "visitor" ? "visitorLogin" : "teacherLogin",
      student: "studentLogin",
      parent: "parentLogin",
    };

    const selectedScreen = screenMap[userType];

    if (selectedScreen) {
      updateInputField({});
      updateCurrentScreen({ type: selectedScreen });
    }
  };

  const getBackButtonText = () => {
    const buttonTextMap = {
      student: "Student account",
      staff: role === "visitor" ? "Visitor account" : "Teacher account",
      parent: "Family account",
    };

    return buttonTextMap[userType] || "Toddle home";
  };

  const handleSignInWithOTP = async ({
    generateOTP = false,
    skipValidation = false,
  }) => {
    if (!skipValidation) {
      const errorCount = isValid();

      if (errorCount > 0) {
        showErrorToast({ errorCount });
        return;
      }
    }

    if (generateOTP) {
      let isSuccess = false;

      if (isPhoneLoginEnabledForParent && shouldRenderPhoneInput) {
        isSuccess = await generateOtpForSignIn({
          userType,
          email,
        });
      } else if (isOTPLoginEnabled) {
        isSuccess = await generateEmailOTPForSignIn({
          userType,
          email,
        });
      } else if (!shouldRenderPhoneInput && isPhoneLoginEnabledForParent) {
        updateInputField({
          shouldRenderPhoneInput: true,
        });
      }

      if (isSuccess) {
        updateInputField({
          otp: "",
          shouldShowOTPField: true,
          shouldShowPasswordField: false,
          shouldShowChangeEmailOption: true,
        });

        startTimer();
        setIsResendEnabled(false);
      }
    } else {
      if (shouldRenderPhoneInput) {
        updateInputField({ phoneNo, otp }, "loginFormV2");
        verifyParentOTPForSignIn();
      } else {
        onSignInWithOTPClick();
      }
    }
  };

  const handleSignInWithPassword = async () => {
    const errorCount = isValid();

    if (errorCount > 0) {
      showErrorToast({ errorCount });
      return;
    }
    onSignInClick();
  };

  const handleSignInWithPasswordClick = ({ skipValidation }) => {
    if (!skipValidation) {
      const errorCount = isValid();

      if (errorCount > 0) {
        showErrorToast({ errorCount });
        return;
      }
    }

    updateInputField({
      shouldShowOTPField: false,
      shouldShowPasswordField: true,
      shouldRenderPhoneInput: false,
      shouldShowChangeEmailOption: true,
      otp: "",
      password: "",
    });
  };

  const handleForgotPassword = () => {
    const params =
      userType === "staff" && role === "visitor"
        ? { type: "forgotPassword", paramsToBeRemoved: ["role"] }
        : { type: "forgotPassword" };
    updateCurrentScreen(params);
  };

  const getLabelText = () => {
    return isEmailAndPhoneLoginEnabled
      ? "Sign in with email/phone"
      : isPhoneLoginEnabledForParent
      ? "Sign in with phone"
      : "Sign in with email";
  };

  const getLabelHelperText = () => {
    return isEmailAndPhoneLoginEnabled
      ? "Email/phone*"
      : isPhoneLoginEnabledForParent
      ? "Phone*"
      : "Email*";
  };

  const getPlaceholderText = () => {
    return isEmailAndPhoneLoginEnabled
      ? "Enter your email id or phone number"
      : isPhoneLoginEnabledForParent
      ? "Enter your phone number"
      : "Enter your email id";
  };

  const updatePhoneInput = (
    { phoneNo },
    {
      dialCode,
      e: {
        nativeEvent: { data },
      },
    }
  ) => {
    updateInputField({ phoneNo }, "parentLogin");
    updateInputField({ phoneNo, email: phoneNo.slice(dialCode.length) });

    if (data && isNaN(parseInt(data))) {
      updateInputField({ email: phoneNo.slice(dialCode.length) + data });
    }
  };

  const checkPhoneVerification = value => {
    return value.length < 6 ? "Please enter a valid phone number" : "";
  };

  const containerStyle = {
    paddingTop: isOrgWorkspaceLoginPage ? "13%" : "19.5%",
  };

  useEffect(() => {
    if (isPhoneLoginEnabledForParent) {
      updateInputField({
        shouldRenderPhoneInput: !!(
          (email && !isNaN(email)) ||
          (!email && phoneNo && !isNaN(phoneNo))
        ),
      });
    }
  }, [phoneNo, email]);

  useEffect(() => {
    setResendTimer(resendTimer);
  }, [resendTimer]);

  useEffect(() => {
    if (!userType || !_.includes(userTypes, userType)) {
      updateCurrentScreen({
        type: "loginHome",
        paramsToBeRemoved: ["usertype", "role"],
      });
      return;
    }

    if (
      (!isParent && isPasswordLoginEnabled && !isOTPLoginEnabled) ||
      (isParent &&
        isPasswordLoginEnabled &&
        !isOTPLoginEnabled &&
        !isLoginWithPhoneEnabled)
    ) {
      updateInputField({
        shouldShowPasswordField: true,
        shouldShowChangeEmailOption: false,
      });
    }

    if (
      (!isOTPLoginEnabled && !isPasswordLoginEnabled && !isParent) ||
      (isParent &&
        !isPasswordLoginEnabled &&
        !isOTPLoginEnabled &&
        !isLoginWithPhoneEnabled)
    ) {
      onBackClick();
    }
  }, []);

  return (
    <div className={classes.container} style={containerStyle}>
      <div>
        {renderBackButtonComponent({
          onClick: onBackClick,
          buttonText: getBackButtonText(),
          ariaLabel: `Go back to ${role || userType} login options`,
        })}
        <div style={headerStyleV2}>{getLabelText()}</div>
        <div className={classes.innerContainer}>
          <div
            className={classes.inputsContainer}
            style={{ gap: shouldRenderPhoneInput ? "16px" : "32px" }}
          >
            <div className={classes.inputContainer}>
              {isPhoneLoginEnabledForParent && shouldRenderPhoneInput ? (
                <>
                  <div className={classes.labelContainer}>
                    <div className="text-label-2">{getLabelHelperText()}</div>
                    {shouldShowOTPField ? (
                      <Button
                        dsVersion="2.0"
                        variant="neutral"
                        type="inline"
                        size="large"
                        onClick={() => {
                          updateInputField({
                            shouldShowOTPField: false,
                            shouldShowPasswordField: false,
                            shouldShowChangeEmailOption: false,
                            otp: "",
                            password: "",
                          });
                        }}
                      >
                        Change phone number
                      </Button>
                    ) : null}
                  </div>
                  <PhoneInputField
                    placeholder={"Enter your phone number"}
                    value={phoneNo}
                    name={"phoneNo"}
                    key={"phoneNo"}
                    renderErrorComponent={renderErrorComponent}
                    updateInputField={updatePhoneInput}
                    showErrorText={true}
                    editContainerStyle={styles.editContainerStyle}
                    disabled={shouldShowOTPField}
                    ref={ref => updateInputRef("phoneNo", ref)}
                    customValidation={checkPhoneVerification}
                    inputProps={{
                      autoFocus: true,
                    }}
                  />
                </>
              ) : (
                <FormTextInput
                  dsVersion="2.0"
                  value={email}
                  name="email"
                  size="large"
                  autoFocus={true}
                  textInputLabel={getLabelHelperText()}
                  placeholder={getPlaceholderText()}
                  testId={"login-email"}
                  customValidation={checkEmailVerification}
                  renderErrorComponent={renderErrorComponent}
                  disabled={shouldShowChangeEmailOption}
                  onEnter={() => handleSignInWithOTP({ generateOTP: true })}
                  ref={ref => updateInputRef("email", ref)}
                  updateInputField={({ email }) => {
                    if (isPhoneLoginEnabledForParent) {
                      updateInputField({ phoneNo: email });
                    }
                    updateInputField({ email });
                  }}
                  labelSuffix={
                    shouldShowChangeEmailOption ? (
                      <Button
                        dsVersion="2.0"
                        variant="neutral"
                        type="inline"
                        size="large"
                        onClick={() => {
                          updateInputField({
                            shouldShowOTPField: false,
                            shouldShowPasswordField: false,
                            shouldShowChangeEmailOption: false,
                            otp: "",
                            password: "",
                          });
                        }}
                      >
                        Change email
                      </Button>
                    ) : null
                  }
                />
              )}
            </div>
            {shouldShowOTPField ? (
              <div className={classes.inputContainer}>
                <FormTextInput
                  dsVersion="2.0"
                  textInputLabel="OTP*"
                  value={otp}
                  name="otp"
                  type="password"
                  size="large"
                  autoComplete="off"
                  autoFocus={true}
                  letterSpacing="10px"
                  placeholder={"• • • • • •"}
                  error={"Enter valid OTP"}
                  ref={ref => updateInputRef("otp", ref)}
                  updateInputField={({ otp }) => {
                    updateInputField({ otp });
                  }}
                  labelSuffix={
                    !shouldRenderPhoneInput &&
                    isPasswordLoginEnabled && (
                      <Button
                        dsVersion="2.0"
                        variant="neutral"
                        type="inline"
                        size="large"
                        onClick={() =>
                          handleSignInWithPasswordClick({
                            skipValidation: true,
                          })
                        }
                      >
                        Sign in using password
                      </Button>
                    )
                  }
                  onEnter={handleSignInWithOTP}
                  aria-label="Enter OTP"
                  testId={"login-otp"}
                />
                <div className={classes.middleContainer}>
                  <Button
                    className={`${classes.forgotPasswordBtn} text-label-3`}
                    size="small"
                    variant={"inline-progressive"}
                    disabled={!isResendEnabled}
                    icon={<RefreshArrowOutlined variant="link" />}
                    onClick={() =>
                      handleSignInWithOTP({
                        generateOTP: true,
                        skipValidation: true,
                      })
                    }
                    aria-label="Send otp again"
                  >
                    {`Send again ${isResendEnabled ? "" : `(${timer}s)`}`}
                  </Button>
                </div>
              </div>
            ) : null}
            {shouldShowPasswordField ? (
              <div className={classes.inputContainer}>
                <FormTextInput
                  dsVersion="2.0"
                  textInputLabel="Password*"
                  placeholder="Enter Password"
                  value={password}
                  name="password"
                  type="password"
                  size="large"
                  autoComplete="off"
                  autoFocus={true}
                  error={"Enter password"}
                  testId={"login-password"}
                  onEnter={handleSignInWithPassword}
                  ref={ref => updateInputRef("password", ref)}
                  updateInputField={({ password }) => {
                    updateInputField({ password });
                  }}
                  labelSuffix={
                    isOTPLoginEnabled && (
                      <Button
                        dsVersion="2.0"
                        variant="neutral"
                        type="inline"
                        size="large"
                        onClick={() => {
                          handleSignInWithOTP({
                            generateOTP: true,
                            skipValidation: true,
                          });
                        }}
                      >
                        Sign in using OTP
                      </Button>
                    )
                  }
                />
                <div className={classes.middleContainer}>
                  <Button
                    dsVersion="2.0"
                    className={classes.forgotPasswordBtn}
                    type="inline"
                    size="medium"
                    onClick={handleForgotPassword}
                  >
                    <div className="text-label-3"> Forgot password?</div>
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
          <div className={classes.bottomContainer}>
            {!shouldShowOTPField &&
            !shouldShowPasswordField &&
            (isOTPLoginEnabled || isPhoneLoginEnabledForParent) ? (
              <Button
                dsVersion="2.0"
                variant="primary"
                type="fill"
                size="large"
                isFullWidth={true}
                style={{ marginTop: shouldRenderPhoneInput ? "-16px" : "0px" }}
                onClick={() => {
                  handleSignInWithOTP({ generateOTP: true });
                }}
                testId="login-get-otp"
              >
                Get OTP
              </Button>
            ) : null}

            {(shouldShowOTPField &&
              (isOTPLoginEnabled || isPhoneLoginEnabledForParent)) ||
            (shouldShowPasswordField && isPasswordLoginEnabled) ? (
              <Button
                dsVersion="2.0"
                variant="primary"
                type="fill"
                size="large"
                isFullWidth={true}
                onClick={
                  shouldShowPasswordField && isPasswordLoginEnabled
                    ? handleSignInWithPassword
                    : handleSignInWithOTP
                }
                testId={"login-submit"}
              >
                Sign in
              </Button>
            ) : null}

            {!shouldShowOTPField &&
            !shouldShowPasswordField &&
            !shouldRenderPhoneInput &&
            isPasswordLoginEnabled &&
            (isOTPLoginEnabled || isPhoneLoginEnabledForParent) ? (
              <div className={classes.buttonContainer}>
                <Button
                  dsVersion="2.0"
                  variant="neutral"
                  type="inline"
                  size="large"
                  onClick={handleSignInWithPasswordClick}
                  testId={"login-using-password"}
                >
                  Or sign in using password
                </Button>
              </div>
            ) : null}
          </div>
        </div>
      </div>
      <div>
        {renderTradeMarkText({
          align: "left",
        })}
      </div>
    </div>
  );
};

export default LoginFormV3;
